/* =========================================================================
   ClueVine — today's garden (pure)
   One plot per enabled mode for a given day: bloomed (won), wilted (lost),
   growing (started, parked), or still a seed. Drives the Home garden strip,
   the archive day sheet, the "Up next" button, and the garden share text.
   ========================================================================= */

import type { AnyPuzzleMode } from '../engine/types';
import type { GardenPlot } from '../utils/shareText';
import { dailyRecord, type DailyRecord, type StatsState } from './statsLogic';

export interface GardenEntry {
  mode: AnyPuzzleMode;
  record?: DailyRecord;
  /** Started but not finished (a parked slot exists). */
  growing: boolean;
  plot: GardenPlot;
}

export interface Garden {
  day: number;
  entries: GardenEntry[];
  won: number;
  played: number;
  total: number;
  complete: boolean;
}

export function dailySlotKey(modeId: string, day: number): string {
  return `${modeId}:d${day}`;
}

export function gardenFor(
  stats: StatsState,
  day: number,
  modes: readonly AnyPuzzleMode[],
  parkedKeys: ReadonlySet<string> = new Set(),
): Garden {
  const entries = modes.map((mode): GardenEntry => {
    const record = dailyRecord(stats, day, mode.id);
    return {
      mode,
      record,
      growing: !record && parkedKeys.has(dailySlotKey(mode.id, day)),
      plot: record ? record.status : 'open',
    };
  });
  const won = entries.filter((e) => e.plot === 'won').length;
  const played = entries.filter((e) => e.plot !== 'open').length;
  return { day, entries, won, played, total: entries.length, complete: entries.length > 0 && played === entries.length };
}

/**
 * The next daily to play after `currentId`: puzzles already started come first,
 * then untouched ones, scanning forward in menu order (wrapping around).
 */
export function nextUnplayed(garden: Garden, currentId?: string): AnyPuzzleMode | null {
  const open = garden.entries.filter((e) => e.plot === 'open' && e.mode.id !== currentId);
  if (open.length === 0) return null;
  const growing = open.find((e) => e.growing);
  if (growing) return growing.mode;
  const idx = garden.entries.findIndex((e) => e.mode.id === currentId);
  const ordered = idx < 0 ? open : [...open.filter((e) => garden.entries.indexOf(e) > idx), ...open];
  return ordered[0]?.mode ?? null;
}
